import { RolePermission } from '@/shared/types/role';

import {
  CONVERSATION_GROUP_KEY,
  CONVERSATION_SCOPE_PERMISSIONS,
  CONVERSATION_SCOPES_BY_REACH,
  ConversationScope,
  EnvironmentScope,
  familyVerbs,
  familyVerbsInGroup,
  LEVEL_VERBS,
  levelAtLeast,
  levelHasExtras,
  levelVerbsForFamily,
  maxGroupLevel,
  PERMISSION_GROUPS,
  PermissionGroup,
  PermissionLevel,
  PermissionToggle,
  PermissionVerb,
  READ_BRAIN,
} from './permissionCatalog';

export const ALL_RESOURCES = '*';

export const CATALOG_PERMISSIONS = new Set(
  PERMISSION_GROUPS.flatMap((group) => [
    ...group.families.flatMap((family) => familyVerbs(family).map((verb) => `${family}.${verb}`)),
    ...(group.toggles ?? []).flatMap((toggle) =>
      toggle.owns.verbs.map((verb) => `${toggle.owns.family}.${verb}`)
    ),
    ...(group.scope === 'conversations' ? [...CONVERSATION_SCOPE_PERMISSIONS.all, READ_BRAIN] : []),
  ])
);

const OWNER_ONLY_GROUPS = PERMISSION_GROUPS.filter((group) => group.ownerOnly);

const OWNER_ONLY_TOGGLES = PERMISSION_GROUPS.flatMap((group) =>
  (group.toggles ?? []).filter((toggle) => toggle.ownerOnly)
);

const OWNER_ONLY_LABEL_KEYS = new Map<string, string>([
  ...OWNER_ONLY_GROUPS.flatMap((group) =>
    group.families.flatMap((family) =>
      familyVerbs(family).map((verb): [string, string] => [`${family}.${verb}`, group.labelKey])
    )
  ),
  ...OWNER_ONLY_TOGGLES.flatMap((toggle) =>
    toggle.owns.verbs.map((verb): [string, string] => [
      `${toggle.owns.family}.${verb}`,
      toggle.labelKey,
    ])
  ),
]);

export const OWNER_ONLY_PERMISSIONS = [...OWNER_ONLY_LABEL_KEYS.keys()];

export type RoleAccess = {
  levels: Record<string, PermissionLevel>;
  toggles: Record<string, boolean>;
  conversationScope: ConversationScope;
  aiAgentConversations: boolean;
  environmentScope: EnvironmentScope;
  environmentIds: string[];
};

export type PermissionGrant = { permission: string; resource_id: string };

const grantKey = ({ permission, resource_id }: PermissionGrant) => `${permission}@${resource_id}`;

const familyOf = (permission: string) => permission.split('.')[0];

const SCOPED_FAMILIES = new Set<string>(
  PERMISSION_GROUPS.flatMap((group) => group.scopedFamilies ?? [])
);

const groupPermissions = (group: PermissionGroup, level: PermissionLevel): string[] =>
  group.families.flatMap((family) =>
    levelVerbsForFamily(group, family, level).map((verb) => `${family}.${verb}`)
  );

const toggleVerbs = (toggle: PermissionToggle, level: PermissionLevel): PermissionVerb[] => {
  if (!levelHasExtras(level)) {
    return [];
  }
  const available = familyVerbs(toggle.owns.family);
  return toggle.owns.verbs.filter(
    (verb) => LEVEL_VERBS[level].includes(verb) && available.includes(verb)
  );
};

const togglePermissions = (
  group: PermissionGroup,
  toggles: Record<string, boolean>,
  level: PermissionLevel
): string[] =>
  (group.toggles ?? [])
    .filter((toggle) => toggles[toggle.key])
    .flatMap((toggle) => toggleVerbs(toggle, level).map((verb) => `${toggle.owns.family}.${verb}`));

const readLevel = (granted: Set<string>, group: PermissionGroup): PermissionLevel => {
  const holds = (verb: PermissionVerb) =>
    group.families.some(
      (family) =>
        familyVerbsInGroup(group, family).includes(verb) && granted.has(`${family}.${verb}`)
    );

  if (group.levelSet === 'chat') {
    if (holds('write')) {
      return 'reply';
    }
    return holds('read') ? 'read' : 'no_access';
  }

  if (holds('delete') || holds('write')) {
    return 'edit';
  }
  return holds('read') ? 'read' : 'no_access';
};

const readScope = (granted: Set<string>): ConversationScope => {
  const score = (scope: ConversationScope) => {
    const needed = CONVERSATION_SCOPE_PERMISSIONS[scope];
    const missing = needed.filter((permission) => !granted.has(permission)).length;
    return needed.length - missing * 2;
  };

  return CONVERSATION_SCOPES_BY_REACH.reduce((best, scope) =>
    score(scope) > score(best) ? scope : best
  );
};

const allowedRows = (permissions: RolePermission[]): RolePermission[] => {
  const denies = permissions.filter((row) => !row.allow);
  const deniedEverywhere = new Set(
    denies.filter((row) => row.resource_id === ALL_RESOURCES).map((row) => row.permission)
  );
  const deniedGrants = new Set(denies.map(grantKey));

  return permissions.filter(
    (row) => row.allow && !deniedEverywhere.has(row.permission) && !deniedGrants.has(grantKey(row))
  );
};

const readEnvironmentIds = (rows: RolePermission[]): string[] => {
  const scoped = rows.filter((row) => SCOPED_FAMILIES.has(familyOf(row.permission)));
  if (scoped.some((row) => row.resource_id === ALL_RESOURCES)) {
    return [];
  }
  return [...new Set(scoped.map((row) => row.resource_id))];
};

const readToggle = (toggle: PermissionToggle, level: PermissionLevel, granted: Set<string>) => {
  const verbs = toggleVerbs(toggle, level);
  if (verbs.length === 0) {
    return false;
  }
  const holds = (verb: PermissionVerb) => granted.has(`${toggle.owns.family}.${verb}`);

  return toggle.ownerOnly ? verbs.every(holds) : verbs.some(holds);
};

export const permissionsToAccess = (permissions: RolePermission[]): RoleAccess => {
  const rows = allowedRows(permissions);
  const granted = new Set(rows.map((row) => row.permission));
  const levels = Object.fromEntries(
    PERMISSION_GROUPS.map((group) => [group.key, readLevel(granted, group)])
  );
  const environmentIds = readEnvironmentIds(rows);

  return {
    levels,
    toggles: Object.fromEntries(
      PERMISSION_GROUPS.flatMap((group) =>
        (group.toggles ?? []).map((toggle) => [
          toggle.key,
          readToggle(toggle, levels[group.key], granted),
        ])
      )
    ),
    conversationScope: readScope(granted),
    aiAgentConversations: granted.has(READ_BRAIN),
    environmentScope: environmentIds.length > 0 ? 'selected' : 'all',
    environmentIds,
  };
};

export const presetAccess = (): RoleAccess => ({
  levels: Object.fromEntries(
    PERMISSION_GROUPS.map((group) => [
      group.key,
      group.ownerOnly ? 'no_access' : maxGroupLevel(group),
    ])
  ),
  toggles: Object.fromEntries(
    PERMISSION_GROUPS.flatMap((group) => group.toggles ?? []).map((toggle) => [
      toggle.key,
      !toggle.ownerOnly,
    ])
  ),
  conversationScope: 'all',
  aiAgentConversations: true,
  environmentScope: 'all',
  environmentIds: [],
});

// Labels for the owner-only permissions a non-owner cannot copy, in catalog order.
export const ownerOnlyLabelKeys = (permissions: RolePermission[]): string[] => {
  const granted = new Set(allowedRows(permissions).map((row) => row.permission));
  const keys = [...OWNER_ONLY_LABEL_KEYS]
    .filter(([permission]) => granted.has(permission))
    .map(([, labelKey]) => labelKey);

  return [...new Set(keys)];
};

export const uncataloguedGrants = (permissions: RolePermission[]): PermissionGrant[] => {
  const grants = allowedRows(permissions)
    .filter((row) => !CATALOG_PERMISSIONS.has(row.permission))
    .map(({ permission, resource_id }) => ({ permission, resource_id }));

  return [...new Map(grants.map((grant) => [grantKey(grant), grant])).values()];
};

export const stripOwnerOnly = (access: RoleAccess): RoleAccess => ({
  ...access,
  levels: {
    ...access.levels,
    ...Object.fromEntries(OWNER_ONLY_GROUPS.map((group) => [group.key, 'no_access'])),
  },
  toggles: {
    ...access.toggles,
    ...Object.fromEntries(OWNER_ONLY_TOGGLES.map((toggle) => [toggle.key, false])),
  },
});

const ENVIRONMENTS_GROUP_KEY = PERMISSION_GROUPS.find(
  (group) => group.scope === 'environments'
)?.key;

export const isScopeIncomplete = (access: RoleAccess) =>
  access.environmentScope === 'selected' &&
  access.environmentIds.length === 0 &&
  (access.levels[ENVIRONMENTS_GROUP_KEY] ?? 'no_access') !== 'no_access';

const narrowedTo = (access: RoleAccess): string[] | null =>
  access.environmentScope === 'selected' && access.environmentIds.length > 0
    ? access.environmentIds
    : null;

const accountWide = (permission: string): PermissionGrant => ({
  permission,
  resource_id: ALL_RESOURCES,
});

export const accessToPermissions = (access: RoleAccess): PermissionGrant[] => {
  const narrowed = narrowedTo(access);

  const grants = PERMISSION_GROUPS.flatMap((group) => {
    const level = access.levels[group.key] ?? 'no_access';
    const names = [
      ...groupPermissions(group, level),
      ...togglePermissions(group, access.toggles, level),
    ];
    const scopable = new Set<string>(group.scope === 'environments' ? group.scopedFamilies : []);

    return names.flatMap((permission) => {
      if (!narrowed || !scopable.has(familyOf(permission))) {
        return [accountWide(permission)];
      }
      return narrowed.map((resource_id) => ({ permission, resource_id }));
    });
  });

  const chatLevel = access.levels[CONVERSATION_GROUP_KEY] ?? 'no_access';
  if (levelAtLeast(chatLevel, 'read')) {
    grants.push(...CONVERSATION_SCOPE_PERMISSIONS[access.conversationScope].map(accountWide));
    if (access.aiAgentConversations) {
      grants.push(accountWide(READ_BRAIN));
    }
  }

  const byKey = new Map(grants.map((grant) => [grantKey(grant), grant]));

  return [...byKey.values()].sort((a, b) => (grantKey(a) < grantKey(b) ? -1 : 1));
};

export type PermissionDiff = { added: PermissionGrant[]; removed: RolePermission[] };

export const diffPermissions = (
  current: RolePermission[],
  desired: PermissionGrant[],
  protectedPermissions: string[] = []
): PermissionDiff => {
  const wanted = new Set(desired.map(grantKey));
  const untouchable = new Set(protectedPermissions);

  const isEditable = (row: RolePermission) =>
    CATALOG_PERMISSIONS.has(row.permission) && !untouchable.has(row.permission);

  const removed = current.filter(
    (row) => isEditable(row) && (!row.allow || !wanted.has(grantKey(row)))
  );

  const removedIds = new Set(removed.map((row) => row.role_permission_id));
  const surviving = new Set(
    current.filter((row) => row.allow && !removedIds.has(row.role_permission_id)).map(grantKey)
  );

  return {
    added: desired.filter(
      (grant) => !surviving.has(grantKey(grant)) && !untouchable.has(grant.permission)
    ),
    removed,
  };
};
